import { useRef, type RefObject } from "react";
import { observer } from "mobx-react-lite";
import { Button } from "@/components/ui/button";
import { OrgLogo } from "@/components/ui/org-logo";
import { AvatarCropDialog } from "@/components/settings/AvatarCropDialog";
import { SettingsSectionLabel } from "@/components/settings/SettingsSectionLabel";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { usePermissions } from "@/hooks/usePermissions";
import {
  LOGO_ACCEPT,
  useLogoCrop,
  useOrgLogo,
  type LogoCrop,
  type LogoRejection,
} from "@/hooks/useOrgLogo";
import type { Organization } from "@/types";
import { toast } from "sonner";
import { Camera, Loader2 } from "lucide-react";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

/**
 * The organization's profile picture. Shown in the organization switcher and
 * embedded in the invitation emails Franklin sends, so an invitee sees who is
 * inviting them.
 *
 * Only an org owner may edit it — the same tier that may rename the org, and
 * what the `org-logos` bucket's RLS enforces.
 */
export const OrganizationLogoSection = observer(function OrganizationLogoSection() {
  const { t } = useLanguage();
  const { isOrgOwner } = usePermissions();
  const { organization, uploading, upload, remove } = useOrgLogo();
  const crop = useLogoCrop(useRejectionToast());

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pickFile = () => fileInputRef.current?.click();

  const hasLogo = Boolean(organization?.logo_url);
  const canEdit = isOrgOwner && Boolean(organization);
  const pickLabel = hasLogo
    ? t.settings.organization.replaceLogo
    : t.settings.organization.uploadLogo;

  return (
    <section className="space-y-4 py-8" {...pageInfoTarget("org.logo")}>
      <SettingsSectionLabel>{t.settings.organization.logo}</SettingsSectionLabel>

      <div className="flex items-center gap-6">
        <LogoButton
          organization={organization}
          canEdit={canEdit}
          uploading={uploading}
          label={pickLabel}
          onPick={pickFile}
        />
        <LogoFileInput inputRef={fileInputRef} onPicked={crop.pick} />

        <div className="space-y-2">
          <LogoBlurb hasLogo={hasLogo} />
          {canEdit && (
            <LogoControls
              pickLabel={pickLabel}
              hasLogo={hasLogo}
              uploading={uploading}
              onPick={pickFile}
              onRemove={remove}
            />
          )}
        </div>
      </div>

      <LogoCropDialog crop={crop} uploading={uploading} onUpload={upload} />
    </section>
  );
});

/** Turns a rejected file into the message that says why it was rejected. */
function useRejectionToast() {
  const { t } = useLanguage();

  return (rejection: LogoRejection) =>
    toast.error(
      rejection === "type"
        ? t.settings.organization.logoInvalidType
        : t.settings.organization.logoTooLarge
    );
}

/** The logo itself, clickable to pick a new one. */
function LogoButton({
  organization,
  canEdit,
  uploading,
  label,
  onPick,
}: {
  organization: Organization | null;
  canEdit: boolean;
  uploading: boolean;
  label: string;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={!canEdit || uploading}
      className="group relative shrink-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed"
      aria-label={label}
      title={label}
    >
      <OrgLogo org={organization} size={80} />
      {canEdit && <LogoOverlay uploading={uploading} />}
    </button>
  );
}

/** Hover/focus affordance over the logo — the same treatment as the avatar. */
function LogoOverlay({ uploading }: { uploading: boolean }) {
  return (
    <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-black/55 text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
      {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
    </span>
  );
}

/** The file picker behind the logo and the upload button. */
function LogoFileInput({
  inputRef,
  onPicked,
}: {
  inputRef: RefObject<HTMLInputElement>;
  onPicked: (file: File) => void;
}) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so picking the same file twice re-fires onChange.
    e.target.value = "";
    if (file) onPicked(file);
  };

  return (
    <input
      ref={inputRef}
      type="file"
      accept={LOGO_ACCEPT}
      className="hidden"
      onChange={handleChange}
    />
  );
}

/** What the logo is for, or — with none set — what setting one would do. */
function LogoBlurb({ hasLogo }: { hasLogo: boolean }) {
  const { t } = useLanguage();

  return (
    <p className="text-sm text-muted-foreground">
      {hasLogo ? t.settings.organization.logoDescription : t.settings.organization.logoEmpty}
    </p>
  );
}

/** Upload / replace the logo, and remove it once there is one. */
function LogoControls({
  pickLabel,
  hasLogo,
  uploading,
  onPick,
  onRemove,
}: {
  pickLabel: string;
  hasLogo: boolean;
  uploading: boolean;
  onPick: () => void;
  onRemove: () => void;
}) {
  const { t } = useLanguage();

  return (
    <div className="flex items-center gap-2">
      <Button type="button" variant="outline" size="sm" onClick={onPick} disabled={uploading}>
        {pickLabel}
      </Button>
      {hasLogo && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onClick={onRemove}
          disabled={uploading}
        >
          {t.settings.organization.removeLogo}
        </Button>
      )}
    </div>
  );
}

/** Crop the picked image to a square, then upload that — never the raw file. */
function LogoCropDialog({
  crop,
  uploading,
  onUpload,
}: {
  crop: LogoCrop;
  uploading: boolean;
  onUpload: (blob: Blob, mime: string) => Promise<boolean>;
}) {
  const { t } = useLanguage();

  if (!crop.src) return null;

  const handleCropped = async (blob: Blob) => {
    const uploaded = await onUpload(blob, crop.mime);
    if (uploaded) crop.close();
  };

  return (
    <AvatarCropDialog
      open={crop.open}
      onOpenChange={(next) => (next ? crop.setOpen(true) : crop.close())}
      imageSrc={crop.src}
      mimeType={crop.mime}
      onCropped={(blob) => void handleCropped(blob)}
      busy={uploading}
      labels={{
        title: t.settings.personal.cropTitle,
        description: t.settings.personal.cropDescription,
        zoom: t.settings.personal.cropZoom,
        save: t.settings.personal.cropSave,
        cancel: t.settings.personal.cropCancel,
      }}
    />
  );
}
